import React from 'react';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {Grid, Chip, Typography, Divider} from '@mui/material';
import Icon from '@mdi/react';
import {mdiFileEditOutline, mdiInboxArrowDown} from '@mdi/js';

import { useTranslation } from "react-i18next";
import {RequestStatusId, UserRole} from '../../ApplicationConfig/constants';
import ManageTeamDrawer from '../Dashboard/Common/ManageTeamDrawer';
import {DialogAnalyst,DialogRequesterDetails} from '../CommonComponents/DialogBox';

const PREFIX = 'AppBarAssignees';

const classes = {
  title: `${PREFIX}-title`,
  upperCase: `${PREFIX}-upperCase`,
  assignee: `${PREFIX}-assignee`,
  icongrey: `${PREFIX}-icongrey`,
  formVerticalDivider: `${PREFIX}-formVerticalDivider`,
  headerBtn: `${PREFIX}-headerBtn`,
  gridDetails: `${PREFIX}-gridDetails`,
  alignCenter: `${PREFIX}-alignCenter`,
  details: `${PREFIX}-details`,
  requestFormHeader: `${PREFIX}-requestFormHeader`
};

const StyledGrid = styled(Grid)((
  {
    theme
  }
) => ({
  [`& .${classes.title}`]: {
    flexGrow: 1,
  },

  [`& .${classes.upperCase}`]: {
    textTransform: 'uppercase',
  },

  [`& .${classes.assignee}`]: {
    display: 'flex',
    alignItems: 'center',
  },

  [`& .${classes.icongrey}`]: {
    marginLeft: theme.spacing(1),
  },

  [`& .${classes.formVerticalDivider}`]: {
    margin: theme.spacing(0, 2, 0, 2),
  },

  [`& .${classes.headerBtn}`]: {
    marginLeft: theme.spacing(1),
  },

  [`& .${classes.gridDetails}`]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    [theme.breakpoints.down('md')]: {
      justifyContent: 'flex-start',
      marginTop: theme.spacing(3),
    },
  },

  [`& .${classes.alignCenter}`]: {
    display: 'flex',
    alignItems: 'center',
  },

  [`& .${classes.details}`]: {
    height: theme.spacing(4),
    display: 'flex',
    alignItems: 'center',
  },

  [`& .${classes.requestFormHeader}`]: {
    display: 'flex',
    flexFlow: 'row',
    alignItems: 'start',
    [theme.breakpoints.down('md')]: {
      flexFlow: 'column',
    },
  }
}));

function PrimaryAssignee({toggleManageTeamDrawer, primaryAnalyst, supportAnalysts}){
  const {t} = useTranslation();

  if(primaryAnalyst.account !== ""){
    return (
      <Chip 
        id="primaryAssignee"
        label={primaryAnalyst.fullName} 
        onClick={(e) => {
          e.stopPropagation();
          toggleManageTeamDrawer();
        }}
        aria-describedby="roleAssigneeLabel"
      />
    );
  }else if(primaryAnalyst.account === "" && supportAnalysts.length !== 0){
    return (
      (<>
        <Typography id="primaryAssignee" variant="body2" color="textSecondary" display="inline">
          {t("requestForm.noLead")}
        </Typography>
      </>)
    );
  } else {
    return (
      <Typography id="primaryAssignee" variant="body2" color="textSecondary">
        {t("requestForm.unassigned")}
      </Typography>
    );
  }
}
PrimaryAssignee.propTypes={
  toggleManageTeamDrawer: PropTypes.func.isRequired,
  primaryAnalyst: PropTypes.object.isRequired,
  supportAnalysts: PropTypes.arrayOf(PropTypes.object).isRequired
};

function SecondaryAssignee({toggleManageTeamDrawer, supportAnalysts, userInfo}){
  const {t} = useTranslation();

  if(supportAnalysts.length !== 0 && userInfo.role!==UserRole.Researcher){
    return (
      <Chip
        id="supportAssignees"
        sx={{ml: 1}}
        label={t("requestForm.support", {count:supportAnalysts.length})}
        onClick={(e) => {
          e.stopPropagation();
          toggleManageTeamDrawer();
        }}
        aria-describedby="roleAssigneeLabel"
      />
    );
  }
  return (
    <></>
  );
}
SecondaryAssignee.propTypes={
  toggleManageTeamDrawer: PropTypes.func.isRequired,
  supportAnalysts: PropTypes.arrayOf(PropTypes.object).isRequired,
  userInfo: PropTypes.object.isRequired
};

function AppBarAssignees(props){

  const {t,i18n} = useTranslation();
  const [open, setOpen] = React.useState({
    dialogRequester: false,
    dialogAssignee: false,
    dialogManageTeam: false
  })
  const data = {
    'statusId':props.request.status.id,
    'requestId':props.request.id,
    'leadAnalyst':props.request.primaryAnalyst,
    'supportAnalysts':props.request.supportAnalysts
  };

  const toggleDialog=(state, value)=>{
    setOpen({...open, [state]: value});
  }
  
  return <>
    <StyledGrid container className={classes.requestFormHeader}>
      <Grid item className={classes.title}>
        <Typography variant="caption" component="p">
          {t("requestForm.project")} {props.request.contractNumber} · {t("requestForm.request")}
          &nbsp;<span id="requestIdentifier">{props.request.identifier}</span>
        </Typography>
        <Typography id="requestTitle" variant="h5" component="h2">
          {props.requestName}
        </Typography>
      </Grid>
      <Grid item className={classes.gridDetails}>
        <Divider
          sx={{display: {xs: 'none', md: 'block'}}}
          orientation="vertical"
          className={classes.formVerticalDivider}
          flexItem
        />
        <Grid item className={classes.alignCenter}>
          <div>
            <Typography variant="caption" component="p">
              {t("requestForm.status")}
            </Typography>
            <Grid className={clsx(classes.alignCenter, classes.details)}>
              <Icon path={props.request.status.id === RequestStatusId.Draft ? mdiFileEditOutline : mdiInboxArrowDown} size={1} />
              <Typography id="requestStatus" variant="body2" component="p" className={classes.icongrey}>
                {i18n.language === "fr" ? props.request.status.nameFr : props.request.status.nameEn}
              </Typography>
            </Grid>
          </div>
          <Divider
            orientation="vertical"
            className={classes.formVerticalDivider}
            flexItem
          />
          <Grid item>
            <div>
              <Typography variant="caption" component="p" id="roleRequesterLabel">
                {t("requestForm.requester")}
              </Typography>
              <div className={classes.details}>
                <Chip
                  label={props.request.researcher.fullName}
                  aria-describedby="roleRequesterLabel"
                  onClick={()=>toggleDialog('dialogRequester', true)}
                />
              </div>
            </div>
          </Grid>
        </Grid>
        <Divider
          orientation="vertical"
          className={classes.formVerticalDivider}
          flexItem
        />
        <Grid item className={classes.assignee}>
          <div>
            <Typography variant="caption" component="p" id="roleAssigneeLabel">
              {t("requestForm.assignee")}
            </Typography>
            <div className={classes.details} >
              <PrimaryAssignee 
                toggleManageTeamDrawer={props.userInfo.role !== UserRole.Researcher ? 
                  ()=>toggleDialog("dialogManageTeam", true) : 
                  ()=>toggleDialog("dialogAssignee", true)
                }
                primaryAnalyst={props.request.primaryAnalyst}
                supportAnalysts={props.request.supportAnalysts}
              />
              <SecondaryAssignee 
                toggleManageTeamDrawer={()=>toggleDialog("dialogManageTeam", true)}
                supportAnalysts={props.request.supportAnalysts}
                userInfo={props.userInfo}
              />
            </div>
          </div>
        </Grid>
      </Grid>
    </StyledGrid>
    <DialogRequesterDetails
      toggleDialog={()=>toggleDialog('dialogRequester', false)}
      open={open.dialogRequester}
      account={props.request.researcher}
    />
    <ManageTeamDrawer
      open={open.dialogManageTeam}
      clickHandler={()=>toggleDialog("dialogManageTeam", false)}
      data={data}
      refreshRequest={props.handleRefreshForm}
      setBackdropOpen={props.setBackdropOpen}
    />
    {props.request.primaryAnalyst.account !== "" &&
      <DialogAnalyst
        open={open.dialogAssignee}
        toggleDialog={()=>toggleDialog('dialogAssignee', false)}
        header={t("dialog.assigneeDetailsTitle")}
        account={props.request.primaryAnalyst}
      />
    }
  </>;
}
AppBarAssignees.propTypes={
  request: PropTypes.object.isRequired,
  requestName: PropTypes.string.isRequired,
  handleRefreshForm: PropTypes.func.isRequired,
  userInfo: PropTypes.object.isRequired,
  setBackdropOpen: PropTypes.func.isRequired
};

export default React.memo(AppBarAssignees);